import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Siren, ArrowRight, CalendarCheck } from "lucide-react";
import { CONTACT } from "@/data/content";
import { CATEGORIES, SERVICES, ZONES } from "@/data/pages";
import { useBooking } from "@/context/BookingContext";
import { AnimatedLines } from "@/components/landing/AnimatedLines";
import { Logo } from "@/components/landing/Logo";

export const Footer = ({ onReserve }) => {
  const { openBooking } = useBooking();
  const servicesByCategory = useMemo(() => {
    const map = {};
    for (const s of SERVICES) {
      (map[s.category] = map[s.category] || []).push(s);
    }
    return map;
  }, []);
  return (
    <footer id="contact" className="relative overflow-hidden bg-ink text-white scroll-mt-44">
      <AnimatedLines variant="dark" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="grid lg:grid-cols-[1.3fr_1fr_1fr] gap-12">
          <div>
            <div className="mb-5">
              <Logo variant="white" imgClassName="h-12 w-auto" />
            </div>
            <p className="text-white/60 text-sm max-w-sm leading-relaxed">
              Nettoyage technique & restauration de surfaces. Auto detailing et entretien
              extérieur de l'habitat. {CONTACT.slogan}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid="footer-book-btn"
                onClick={openBooking}
                className="cta inline-flex items-center gap-2 bg-royal text-white font-extrabold px-6 py-3 rounded-sm hover:bg-royal-hover"
              >
                <CalendarCheck className="w-4 h-4" /> Réserver mon créneau
              </button>
              <button
                data-testid="footer-quote-btn"
                onClick={() => onReserve("auto")}
                className="cta inline-flex items-center gap-2 border border-white/30 text-white font-bold px-6 py-3 rounded-sm hover:bg-white hover:text-ink"
              >
                Obtenir mon devis <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-white/50 mb-5">
              Contact
            </h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 hover:text-white transition-colors">
                  <Phone className="w-4 h-4 text-white/60" /> {CONTACT.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.quoteEmail}`} data-testid="footer-email" className="flex items-center gap-3 hover:text-white transition-colors break-all">
                  <Mail className="w-4 h-4 text-white/60 shrink-0" /> {CONTACT.quoteEmail}
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/70">
                <MapPin className="w-4 h-4 text-white/60" /> Zone {CONTACT.area}
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-white/50 mb-5">
              Une urgence ?
            </h4>
            <div className="bg-alert/10 border border-alert/40 rounded-sm p-5">
              <div className="flex items-center gap-2 text-alert font-bold">
                <Siren className="w-5 h-5" /> Intervention 24/48h
              </div>
              <p className="text-white/60 text-sm mt-2">
                Sinistre, vandalisme, retour de location LLD/LOA, événement de dernière minute.
              </p>
              <a
                href={CONTACT.phoneHref}
                data-testid="footer-emergency-call"
                className="mt-4 inline-flex items-center gap-2 bg-alert text-white font-bold px-5 py-2.5 rounded-sm hover:bg-alert-hover transition-colors text-sm"
              >
                <Phone className="w-4 h-4" /> Appeler maintenant
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {CATEGORIES.map((cat) => (
            <div key={cat.slug}>
              <Link to={`/${cat.slug}`} className="font-heading font-bold text-sm text-white hover:text-white/70 transition-colors">
                {cat.name}
              </Link>
              <ul className="mt-3 space-y-2">
                {(servicesByCategory[cat.slug] || []).map((s) => (
                  <li key={s.slug}>
                    <Link to={`/${s.slug}`} className="text-sm text-white/55 hover:text-white transition-colors">
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 mt-10 pt-6 flex flex-wrap gap-x-4 gap-y-2">
          {ZONES.map((z) => (
            <Link key={z.slug} to={`/${z.slug}`} className="text-sm font-semibold text-white/70 hover:text-white transition-colors">
              Nettoyage à {z.city}
            </Link>
          ))}
          <Link to="/realisations" className="text-sm font-semibold text-white/70 hover:text-white transition-colors">Réalisations</Link>
          <Link to="/intervention-urgence" className="text-sm font-semibold text-white/70 hover:text-white transition-colors">Urgences 24/48h</Link>
          <Link to="/devis" className="text-sm font-semibold text-white/70 hover:text-white transition-colors">Devis gratuit</Link>
        </div>

        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} MARTIN NETTOYAGE. Tous droits réservés.</span>
          <span>Auto Detailing · Habitat · Entretien continu</span>
        </div>
      </div>
    </footer>
  );
};
